import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { IconArrowLeft } from '@tabler/icons-react';
import { unwrapData, STAFF_EMAIL_DOMAIN } from '@wapharma/shared';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { Button, Card, cx, Avatar, IconButton, Input, Loader, Stack, Text, WP_ICON_NAV } from '@wapharma/design-system';
import { apiPost } from '../api';
import { getDisplayError, HttpError, parseFieldErrorsFromHttpError } from '../apiErrors';
import { AdminAuthLayout } from '../components/auth/AdminAuthLayout';
import authStyles from '../components/auth/auth-layout.module.css';
import { PasswordInput } from '../components/PasswordInput';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { buildLoginWelcome, type LoginWelcomeInput } from '../utils/loginWelcome';
import { authFooterLine, pickLoginTagline, resolveDeployOrgBrand } from '../lib/deployOrgBrand';
import { isAuthPublicReturnPath } from '../auth/publicPaths';
import styles from './LoginPage.module.css';

type LoginStep = 'email' | 'password';
type StepTransition = 'idle' | 'to-password' | 'to-email';

type LoginIdentity = LoginWelcomeInput;

function safeNextDestination(raw: string | null): string {
  if (!raw) return '/';
  let decoded: string;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    return '/';
  }
  if (!decoded.startsWith('/') || decoded.startsWith('//')) return '/';
  const noHash = decoded.split('#')[0] ?? '/';
  if (isAuthPublicReturnPath(noHash)) return '/';
  return noHash;
}

function toStaffLoginEmail(localPart: string): string {
  return `${localPart.trim().toLowerCase()}@${STAFF_EMAIL_DOMAIN}`;
}

function validateLoginUsername(value: string): string | undefined {
  const v = value.trim();
  if (!v) return "L'identifiant est requis.";
  if (v.includes('@') || /\s/.test(v) || !/^[a-zA-Z0-9._%+-]+$/.test(v)) {
    return 'Identifiant invalide.';
  }
  return undefined;
}

function validatePasswordField(value: string): string | undefined {
  if (!value.trim()) return 'Le mot de passe est requis.';
  return undefined;
}

export function LoginPage() {
  const { user, loading, login, mustChangePassword } = useAuth();
  const { showToast, clearToasts } = useToast();
  const [searchParams] = useSearchParams();
  const postLoginTo = useMemo(() => safeNextDestination(searchParams.get('next')), [searchParams]);
  const [step, setStep] = useState<LoginStep>('email');
  const [stepTransition, setStepTransition] = useState<StepTransition>('idle');
  const [slideAnimReady, setSlideAnimReady] = useState(false);
  const [slideWidth, setSlideWidth] = useState(0);
  const [canFocusPassword, setCanFocusPassword] = useState(false);
  const [identity, setIdentity] = useState<LoginIdentity | null>(null);
  const stepViewportRef = useRef<HTMLDivElement>(null);
  const emailPanelRef = useRef<HTMLDivElement>(null);
  const passwordPanelRef = useRef<HTMLDivElement>(null);
  const [viewportHeight, setViewportHeight] = useState<number | null>(null);
  const [username, setUsername] = useState(import.meta.env.DEV ? 'admin' : '');
  const [password, setPassword] = useState('');
  const [serverFieldErrors, setServerFieldErrors] = useState<{ email?: string; password?: string }>({});
  const [emailTouched, setEmailTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [pending, setPending] = useState(false);
  const authInFlightRef = useRef(false);
  const deployBrand = useMemo(() => resolveDeployOrgBrand(), []);
  const [brandQuote] = useState(() => pickLoginTagline(deployBrand));

  const emailError = useMemo(() => {
    if (serverFieldErrors.email) return serverFieldErrors.email;
    if (!emailTouched && !submitAttempted) return undefined;
    return validateLoginUsername(username);
  }, [username, emailTouched, submitAttempted, serverFieldErrors.email]);

  const passwordError = useMemo(() => {
    if (serverFieldErrors.password) return serverFieldErrors.password;
    if (!passwordTouched && !submitAttempted) return undefined;
    return validatePasswordField(password);
  }, [password, passwordTouched, submitAttempted, serverFieldErrors.password]);

  const resolvedEmail = identity?.email ?? toStaffLoginEmail(username);
  const loginWelcome = useMemo(
    () => (identity ? buildLoginWelcome(identity) : null),
    [identity],
  );

  useEffect(() => {
    const frame = requestAnimationFrame(() => setSlideAnimReady(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  const measurePanelHeight = useCallback((panel: HTMLDivElement | null) => {
    if (!panel) return 0;
    return Math.ceil(panel.getBoundingClientRect().height);
  }, []);

  const measureViewportHeight = useCallback(() => {
    const panel = step === 'email' ? emailPanelRef.current : passwordPanelRef.current;
    return measurePanelHeight(panel);
  }, [step, measurePanelHeight]);

  useLayoutEffect(() => {
    if (loading) return;

    const viewport = stepViewportRef.current;
    if (!viewport) return;

    const syncSlideWidth = () => {
      const next = Math.round(viewport.clientWidth);
      setSlideWidth((prev) => (prev === next ? prev : next));
    };

    syncSlideWidth();
    window.addEventListener('resize', syncSlideWidth);
    return () => window.removeEventListener('resize', syncSlideWidth);
  }, [loading]);

  useLayoutEffect(() => {
    if (loading) return;

    if (stepTransition === 'to-password' && identity) {
      const next = measurePanelHeight(passwordPanelRef.current);
      setViewportHeight(next);
      setStep('password');
      setStepTransition('idle');
      return;
    }

    if (stepTransition === 'to-email') {
      const next = measurePanelHeight(emailPanelRef.current);
      setViewportHeight(next);
      setStepTransition('idle');
    }
  }, [loading, stepTransition, identity, measurePanelHeight]);

  useLayoutEffect(() => {
    if (loading || stepTransition !== 'idle') return;

    const syncViewportHeight = () => {
      const next = measureViewportHeight();
      setViewportHeight((prev) => (prev === next ? prev : next));
    };

    syncViewportHeight();

    const emailEl = emailPanelRef.current;
    const passwordEl = passwordPanelRef.current;
    if (!emailEl && !passwordEl) return;

    const ro = new ResizeObserver(() => syncViewportHeight());
    if (emailEl) ro.observe(emailEl);
    if (passwordEl) ro.observe(passwordEl);
    return () => ro.disconnect();
  }, [
    loading,
    stepTransition,
    step,
    loginWelcome,
    emailError,
    passwordError,
    pending,
    measureViewportHeight,
  ]);

  useEffect(() => {
    if (user) clearToasts();
  }, [user, clearToasts]);

  useEffect(() => () => clearToasts(), [clearToasts]);

  useEffect(() => {
    if (step !== 'password' || !identity) return;
    if (slideWidth > 0 && slideAnimReady) return;
    setCanFocusPassword(true);
  }, [step, identity, slideWidth, slideAnimReady]);

  if (!loading && user) {
    if (mustChangePassword) {
      return <Navigate to="/force-password-change" replace />;
    }
    return <Navigate to={postLoginTo} replace />;
  }

  if (loading) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '100dvh', background: 'var(--wp-surface-page)' }}>
        <Loader />
      </div>
    );
  }

  const resetToEmailStep = () => {
    clearToasts();
    setCanFocusPassword(false);
    setStepTransition('to-email');
    setStep('email');
    setPassword('');
    setPasswordTouched(false);
    setSubmitAttempted(false);
    setServerFieldErrors({});
  };

  const handleStepTrackTransitionEnd = (event: React.TransitionEvent<HTMLDivElement>) => {
    if (event.propertyName !== 'transform') return;
    if (event.target !== event.currentTarget) return;
    if (step === 'password') {
      setCanFocusPassword(true);
      return;
    }
    setIdentity(null);
  };

  const continueWithEmail = (e: React.FormEvent) => {
    e.preventDefault();
    if (authInFlightRef.current || pending) return;
    setSubmitAttempted(true);
    setServerFieldErrors({});

    const nextEmailError = validateLoginUsername(username);
    if (nextEmailError) return;

    const loginEmail = toStaffLoginEmail(username);
    authInFlightRef.current = true;
    setPending(true);
    void apiPost<{ data: LoginIdentity }>('/auth/login/identify', {
      email: loginEmail,
      client: 'admin',
    })
      .then((raw) => {
        const d = unwrapData(raw) as LoginIdentity;
        setIdentity(d);
        setUsername(
          (() => {
            const v = d.email.trim();
            const at = v.indexOf('@');
            return (at >= 0 ? v.slice(0, at) : v).toLowerCase();
          })(),
        );
        setPassword('');
        setPasswordTouched(false);
        setSubmitAttempted(false);
        setServerFieldErrors({});
        setCanFocusPassword(false);
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        setStepTransition('to-password');
      })
      .catch((er: unknown) => {
        const parsed = parseFieldErrorsFromHttpError(er);
        if (parsed?.email) {
          setServerFieldErrors({ email: parsed.email });
          return;
        }
        if (er instanceof HttpError && er.status === 403) {
          setServerFieldErrors({ email: getDisplayError(er) });
          return;
        }
        showToast(getDisplayError(er), { tone: 'error' });
      })
      .finally(() => {
        authInFlightRef.current = false;
        setPending(false);
      });
  };

  const submitPassword = (e: React.FormEvent) => {
    e.preventDefault();
    if (authInFlightRef.current || pending) return;
    setSubmitAttempted(true);
    setServerFieldErrors({});
    const normalizedPassword = password.trim();

    const nextPasswordError = validatePasswordField(normalizedPassword);
    if (nextPasswordError) return;

    authInFlightRef.current = true;
    setPending(true);
    void login(resolvedEmail, normalizedPassword)
      .catch((er: unknown) => {
        const parsed = parseFieldErrorsFromHttpError(er);
        if (parsed) {
          setServerFieldErrors({
            email: parsed.email,
            password: parsed.password,
          });
          if (parsed.email) resetToEmailStep();
          if (parsed._form) {
            showToast(parsed._form, { tone: 'error' });
          }
          return;
        }
        const message = getDisplayError(er);
        if (er instanceof HttpError && er.status === 403) {
          setServerFieldErrors({ email: message });
          resetToEmailStep();
          return;
        }
        if (er instanceof HttpError && er.status === 401) {
          setServerFieldErrors({ password: message });
        }
        showToast(message, { tone: 'error', autoDismissMs: 8000 });
      })
      .finally(() => {
        authInFlightRef.current = false;
        setPending(false);
      });
  };

  const slideTransform =
    step === 'password'
      ? slideWidth > 0
        ? `translate3d(calc(-${slideWidth}px - 1px), 0, 0)`
        : 'translate3d(calc(-50% - 1px), 0, 0)'
      : 'translate3d(0, 0, 0)';

  const viewportStyle = {
    ...(slideWidth > 0 ? { '--login-slide-width': `${slideWidth}px` } : {}),
    ...(viewportHeight != null ? { height: viewportHeight } : {}),
  } as React.CSSProperties;

  return (
    <AdminAuthLayout brandBody={brandQuote}>
      <Stack gap="xl" className={authStyles.formStack}>
        <Card elevation="flat" padding="lg" className={authStyles.authFormCard}>
              <div
                ref={stepViewportRef}
                className={cx(
                  styles.stepViewport,
                  slideWidth > 0 && styles.stepViewportMeasured,
                  slideAnimReady && styles.stepViewportAnimated,
                )}
                style={viewportStyle}
              >
                <div
                  className={cx(
                    styles.stepTrack,
                    slideAnimReady && styles.stepTrackAnimated,
                  )}
                  style={{ transform: slideTransform }}
                  onTransitionEnd={handleStepTrackTransitionEnd}
                >
                  <div
                    ref={emailPanelRef}
                    className={styles.stepPanel}
                    data-active={step === 'email'}
                    {...(step !== 'email' ? { inert: true } : {})}
                  >
                    <Stack gap={0}>
                      <div className={authStyles.formHeaderBand}>
                        <div className={authStyles.formHeading}>
                          <h1 className={authStyles.formTitle}>Connexion</h1>
                        </div>
                      </div>

                      <form onSubmit={continueWithEmail} noValidate className={styles.stepBody}>
                        <Stack gap="md">
                          <Input
                            label="Identifiant"
                            type="text"
                            name="username"
                            inputMode="email"
                            autoCapitalize="none"
                            autoCorrect="off"
                            spellCheck={false}
                            autoComplete="username"
                            placeholder="prenom.nom"
                            value={username}
                            suffix={`@${STAFF_EMAIL_DOMAIN}`}
                            error={emailError}
                            tabIndex={step === 'email' ? undefined : -1}
                            onChange={(ev) => {
                              let next = ev.currentTarget.value;
                              const at = next.indexOf('@');
                              if (at >= 0) next = next.slice(0, at);
                              setUsername(next.replace(/\s/g, ''));
                              setEmailTouched(true);
                              if (serverFieldErrors.email) {
                                setServerFieldErrors((prev) => ({ ...prev, email: undefined }));
                              }
                            }}
                            onBlur={() => setEmailTouched(true)}
                          />
                          <Button type="submit" loading={pending} className={authStyles.submitBtn}>
                            {pending ? 'Vérification…' : 'Continuer'}
                          </Button>
                        </Stack>
                      </form>
                    </Stack>
                  </div>

                  <div
                    ref={passwordPanelRef}
                    className={styles.stepPanel}
                    data-active={step === 'password' && !!identity}
                    {...(step !== 'password' || !identity ? { inert: true } : {})}
                  >
                    {identity ? (
                      <Stack gap={0}>
                        <div className={authStyles.formHeaderBand}>
                          <div className={cx(authStyles.formHeading, styles.passwordHeading)}>
                            <IconButton
                              type="button"
                              variant="ghost"
                              size="sm"
                              borderless
                              className={styles.changeEmailBtn}
                              aria-label="Utiliser un autre identifiant"
                              tabIndex={step === 'password' ? undefined : -1}
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={(e) => {
                                e.preventDefault();
                                resetToEmailStep();
                              }}
                            >
                              <IconArrowLeft {...WP_ICON_NAV} aria-hidden />
                            </IconButton>
                            <Avatar
                              label={loginWelcome?.displayName ?? identity.email}
                              src={identity.profilePictureUrl}
                              size="md"
                              className={styles.identityAvatar}
                            />
                            <div className={styles.passwordHeadingText}>
                              <p className={authStyles.formTitle}>
                                {loginWelcome ? (
                                  <>
                                    {loginWelcome.greetingLead},{' '}
                                    <span className={styles.welcomeName}>
                                      {loginWelcome.emphasizedName}
                                    </span>
                                  </>
                                ) : (
                                  'Connexion'
                                )}
                              </p>
                              <p className={styles.welcomeEmail}>{identity.email}</p>
                            </div>
                          </div>
                        </div>

                        <Stack gap="md" className={styles.stepBody}>
                          <form onSubmit={submitPassword} noValidate>
                            <Stack gap="md">
                              <PasswordInput
                                label="Mot de passe"
                                name="password"
                                autoComplete="current-password"
                                placeholder="••••••••"
                                value={password}
                                error={passwordError}
                                autoFocus={canFocusPassword && step === 'password'}
                                tabIndex={step === 'password' ? undefined : -1}
                                onChange={(ev) => {
                                  setPassword(ev.target.value);
                                  setPasswordTouched(true);
                                  if (serverFieldErrors.password) {
                                    setServerFieldErrors((prev) => ({ ...prev, password: undefined }));
                                  }
                                }}
                                onBlur={() => setPasswordTouched(true)}
                              />
                              <Text size="md" ta="right" ff="body" tabIndex={step === 'password' ? undefined : -1}>
                                <Link
                                  to="/forgot-password"
                                  className={authStyles.authNavLink}
                                  tabIndex={step === 'password' ? undefined : -1}
                                >
                                  Mot de passe oublié ?
                                </Link>
                              </Text>
                              <Button type="submit" loading={pending} className={authStyles.submitBtn}>
                                {pending ? 'Connexion…' : 'Se connecter'}
                              </Button>
                            </Stack>
                          </form>
                        </Stack>
                      </Stack>
                    ) : null}
                  </div>
                </div>
              </div>
        </Card>

        <Text size="sm" c="secondary" ta="center" ff="body" className={authStyles.mobileFooter}>
          {authFooterLine(deployBrand)}
        </Text>
      </Stack>
    </AdminAuthLayout>
  );
}
